import { db } from "./dbClient";

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { inviteTeamMember } from "./authFunctions";
import { getCompany, getTeam, saveTeam, getUsers, saveUsers, saveSession } from "./Auth";

const ROLES = ["MLRO", "Deputy MLRO", "Senior AML Analyst", "AML Analyst", "Compliance Officer", "Compliance Manager", "KYC Analyst", "Risk Manager", "CFO", "CEO", "Legal Counsel", "Other"];
const PERMISSION_KEYS = ["admin", "analyst"];

function Field({ label, value, onChange, placeholder, type = "text" }) {
  const C = useTheme();
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>{label.toUpperCase()}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ background: C.bg, border: `1px solid ${focused ? C.accent : C.border}`, borderRadius: 8, color: C.text, fontSize: 14, padding: "10px 14px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box", transition: "border-color 0.15s" }} />
    </div>
  );
}

export default function TeamPage({ currentUser, setCurrentUser, team, setTeam }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [showAdd, setShowAdd]         = useState(false);
  const [form, setForm]               = useState({ firstName: "", lastName: "", email: "", role: "", permission: "admin" });
  const [editId, setEditId]           = useState(null);
  const [error, setError]             = useState("");
  const [inviteStatus, setInviteStatus] = useState(null);
  const [inviteUrl, setInviteUrl]     = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [syncing, setSyncing]         = useState(false);
  const [syncMsg, setSyncMsg]         = useState(null);

  const canInvite  = form.firstName && form.lastName && form.email && form.role;
  const permColor  = { admin: C.accent, analyst: "#F0B429" };

  useEffect(() => {
    loadTeamFromDatabase();
  }, []);

  async function loadTeamFromDatabase() {
    try {
      const company = getCompany();
      if (!company?.id) return;

      const res = await fetch(`/api/auth/get-team-members?companyId=${company.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('amlguard_token') || ''}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.members) {
          setTeam(data.members);
          saveTeam(data.members);
        }
      }
    } catch (err) {
      console.warn('[Team] Failed to sync from database:', err.message);
    }
  }

  async function sendInvite() {
    if (!canInvite) return;
    setError("");
    setInviteStatus("sending");

    if (editId) {
      const updated = team.map(m => m.id === editId ? { ...m, ...form } : m);
      setTeam(updated);
      saveTeam(updated);
      const allUsers = getUsers();
      saveUsers(allUsers.map(u => u.email === form.email ? { ...u, permission: form.permission } : u));

      const localCo = getCompany();
      if (localCo?.id) {
        // Update through API to Neon database
        fetch('/api/auth/update-team-member', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('amlguard_token') || ''}`
          },
          body: JSON.stringify({
            email: form.email,
            companyId: localCo.id,
            permission: form.permission
          })
        })
          .then(res => res.json())
          .then(data => {
            if (data.success) {
              console.log("[Team] permission updated successfully for", form.email);
            } else {
              console.error("[Team] update permission error:", data.error);
              setError(`Failed to update permissions: ${data.error}`);
            }
          })
          .catch(err => {
            console.error("[Team] update permission failed:", err.message);
            setError(`Failed to update permissions: ${err.message}`);
          });
      }

      const editedMember = team.find(m => m.id === editId);
      if (editedMember && currentUser && editedMember.email === currentUser.email) {
        const updatedMe = { ...currentUser, permission: form.permission };
        saveSession(updatedMe);
        if (setCurrentUser) setCurrentUser(updatedMe);
      }
      setEditId(null);
      setForm({ firstName: "", lastName: "", email: "", role: "", permission: "admin" });
      setShowAdd(false);
      setInviteStatus(null);
      return;
    }

    try {
      const company = getCompany();
      if (!company?.id) {
        throw new Error("No company context found. Please refresh and try again.");
      }
      const response = await inviteTeamMember(form, company.id);
      const pending = { ...form, id: `pending_${Date.now()}`, joined: new Date().toISOString().slice(0, 10), status: "invited" };
      const updated = [...team, pending];
      setTeam(updated);
      saveTeam(updated);
      setInviteStatus("sent");
      setInviteUrl(response.inviteUrl);
      setInviteToken(response.inviteToken);
      setForm({ firstName: "", lastName: "", email: "", role: "", permission: "admin" });
      // Keep showing the form so user can see the invite link
      // setTimeout(() => { setShowAdd(false); setInviteStatus(null); }, 2000);
    } catch (err) {
      setError(err.message);
      setInviteStatus("error");
    }
  }

  async function syncTeam() {
    setSyncing(true);
    try {
      const company = getCompany();
      if (!company?.id) throw new Error('No company context');

      const res = await fetch(`/api/auth/get-team-members?companyId=${company.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('amlguard_token') || ''}` }
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Sync failed");

      setTeam(data.members);
      saveTeam(data.members);
      setSyncMsg(t('team.syncSuccess', { count: data.members.length }));
      setTimeout(() => setSyncMsg(null), 3000);
    } catch (err) {
      setSyncMsg(`✗ ${err.message || t('team.syncFailed')}`);
      setTimeout(() => setSyncMsg(null), 4000);
    }
    setSyncing(false);
  }

  function startEdit(m) {
    setForm({ firstName: m.firstName, lastName: m.lastName, email: m.email, role: m.role, permission: m.permission });
    setEditId(m.id);
    setShowAdd(true);
  }

  function remove(id) {
    const updated = team.filter(m => m.id !== id);
    setTeam(updated);
    saveTeam(updated);
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('team.title')}</h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>{t('team.memberCount', { count: team.length })}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {syncMsg && <span style={{ color: syncMsg.startsWith("✓") ? C.success : C.danger, fontSize: 12 }}>{syncMsg}</span>}
          <button onClick={syncTeam} disabled={syncing} style={{ padding: "9px 16px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
            {syncing ? t('team.syncing') : t('team.sync')}
          </button>
          <button onClick={() => { setShowAdd(!showAdd); setEditId(null); setForm({ firstName: "", lastName: "", email: "", role: "", permission: "analyst" }); setInviteStatus(null); setError(""); }}
            style={{ padding: "9px 18px", background: C.accent, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
            {showAdd ? `✕ ${t('common.cancel')}` : `+ ${t('team.inviteMember')}`}
          </button>
        </div>
      </div>

      {showAdd && (
        <div style={{ background: C.surface, border: `1px solid ${C.accent}`, borderRadius: 12, padding: 26, marginBottom: 20 }}>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
            {editId ? t('team.editMember') : t('team.inviteTeamMember')}
          </div>
          {!editId && (
            <div style={{ color: C.textDim, fontSize: 12, marginBottom: 18 }}>
              {t('team.inviteHint')}
            </div>
          )}
          {error && (
            <div style={{ color: C.danger, fontSize: 13, marginBottom: 12, background: "rgba(255,68,68,0.1)", padding: "10px 14px", borderRadius: 6 }}>
              {error.includes("not configured")
                ? t('team.supabaseNotConfigured')
                : error}
            </div>
          )}
          {inviteStatus === "sent" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: C.success, fontSize: 13, marginBottom: 12, background: "rgba(0,230,118,0.08)", padding: "10px 14px", borderRadius: 6 }}>
                ✓ {t('team.inviteSent', { email: form.email || t('team.them') })}
              </div>
              {inviteUrl && (
                <div style={{ background: "rgba(74, 127, 165, 0.1)", border: `1px solid rgba(74, 127, 165, 0.3)`, borderRadius: 6, padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 8, textTransform: "uppercase" }}>📋 Invite Link</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <input
                      type="text"
                      value={inviteUrl}
                      readOnly
                      style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 12, padding: "8px 10px", fontFamily: "'DM Mono', monospace", outline: "none" }}
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(inviteUrl); alert('Link copied!'); }}
                      style={{ padding: "8px 12px", background: C.accent, color: C.bg, border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 700, fontSize: 12, fontFamily: "'DM Mono', monospace" }}
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div style={{ color: C.textDim, fontSize: 11, lineHeight: 1.5 }}>
                    Share this link with {form.firstName} so they can set their password and join the team.
                  </div>
                </div>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <Field label={t('team.firstName')} value={form.firstName} onChange={v => setForm(p => ({ ...p, firstName: v }))} placeholder="Jane" />
            <Field label={t('team.lastName')}  value={form.lastName}  onChange={v => setForm(p => ({ ...p, lastName: v }))}  placeholder="Smith" />
            <Field label={t('team.email')}      value={form.email}     onChange={v => setForm(p => ({ ...p, email: v }))}     placeholder="jane@company.com" />
            <div>
              <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>{t('team.role').toUpperCase()}</div>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "10px 12px", fontFamily: "'DM Mono', monospace", outline: "none" }}>
                <option value="">{t('common.select')}…</option>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 10 }}>{t('team.permission').toUpperCase()}</div>
            <div style={{ display: "flex", gap: 10 }}>
              {PERMISSION_KEYS.map(key => (
                <button key={key} onClick={() => setForm(p => ({ ...p, permission: key }))} style={{ flex: 1, padding: "9px", borderRadius: 7, cursor: "pointer", background: form.permission === key ? `${permColor[key]}22` : C.bg, border: `1px solid ${form.permission === key ? permColor[key] : C.border}`, color: form.permission === key ? permColor[key] : C.textMid, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                  {t(`common.permissions.${key}`)}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={() => { setShowAdd(false); setInviteStatus(null); setError(""); }} style={{ padding: "8px 16px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('common.cancel')}</button>
            <button onClick={sendInvite} disabled={!canInvite || inviteStatus === "sending"} style={{ padding: "8px 18px", background: canInvite && inviteStatus !== "sending" ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: canInvite ? "pointer" : "default", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
              {inviteStatus === "sending" ? t('team.sending') : editId ? t('common.save') : `✉ ${t('team.sendInvite')}`}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {team && team.map(member => {
          const isYou    = currentUser && member.id === currentUser.id;
          const isPending = member.status === "invited";
          return (
            <div key={member.id} style={{ background: C.surface, border: `1px solid ${isPending ? C.borderLight : C.border}`, borderRadius: 9, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, opacity: isPending ? 0.75 : 1 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: isYou ? `linear-gradient(135deg, ${C.accent}, ${C.accentDim})` : `linear-gradient(135deg, ${C.borderLight}, ${C.border})`, display: "flex", alignItems: "center", justifyContent: "center", color: isYou ? C.bg : C.textMid, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {isPending ? "✉" : `${member.firstName?.[0]}${member.lastName?.[0]}`}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{member.firstName} {member.lastName}</span>
                  {isYou    && <span style={{ color: C.accent, fontSize: 10, background: C.accentGlow, padding: "2px 6px", borderRadius: 4, fontFamily: "'DM Mono', monospace" }}>{t('team.you')}</span>}
                  {isPending && <span style={{ color: C.warn, fontSize: 10, background: "rgba(255,140,0,0.1)", border: "1px solid rgba(255,140,0,0.25)", padding: "2px 7px", borderRadius: 4, fontFamily: "'DM Mono', monospace" }}>{t('team.invitePending')}</span>}
                </div>
                <div style={{ color: C.textDim, fontSize: 12, marginTop: 2 }}>{member.email}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: C.text, fontSize: 13, marginBottom: 4 }}>{member.role}</div>
                <span style={{ color: permColor[member.permission], fontSize: 10, background: `${permColor[member.permission]}18`, padding: "2px 8px", borderRadius: 4 }}>{member.permission ? t(`common.permissions.${member.permission}`, { defaultValue: member.permission }) : ""}</span>
              </div>
              {!isYou && (
                <div style={{ display: "flex", gap: 7, marginLeft: 8 }}>
                  {!isPending && currentUser?.permission === "admin" && (
                    <button onClick={() => startEdit(member)} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, color: C.textMid, padding: "5px 11px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>{t('common.edit')}</button>
                  )}
                  {currentUser?.permission === "admin" && (
                    <button onClick={() => remove(member.id)} style={{ background: "transparent", border: `1px solid ${C.danger}`, borderRadius: 5, color: C.danger, padding: "5px 9px", cursor: "pointer", fontSize: 12 }}>✕</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}